import { getColorCssValue } from '../../../../../element/utils/elementColorStyleUtils';
import { getContrastRatio } from '../../../../../../common/colors/coreColorUtil';
import { Color } from '../../../../../../common/colors/colorConstants';
import { ColorButtonProps } from './popup/ColorButton';
import { isObject } from 'lodash';

/**
 * The lowest contrast ratio a swatch can have with the panel behind it before it needs a border
 * to stay distinguishable. Applied to colours read from the DOM, so it holds for every swatch in
 * every theme rather than needing per-colour or per-theme exceptions.
 */
export const MIN_SWATCH_SURFACE_CONTRAST = 1.2;

/**
 * Whether a swatch needs a border to separate it from the panel behind it.
 *
 * A missing colour means nothing measurable is painted (the no-colour and transparent swatches),
 * in which case the panel shows straight through and a border is always needed.
 */
export const getIsLowContrastSwatch = (swatchColor: string | null, surfaceColor: string | null): boolean => {
    if (!swatchColor || !surfaceColor) return true;

    try {
        return getContrastRatio(swatchColor, surfaceColor) <= MIN_SWATCH_SURFACE_CONTRAST;
    } catch {
        return true;
    }
};

/** Whether a predefined colour entry represents no persisted colour (e.g. COLORS.NONE). */
export const isEmptyColorOption = (color: Color): boolean => !color.name && !color.css && !color.hex;

export const isEmptyColorValue = (color: string | Color | null): boolean => {
    if (!color) return true;
    if (typeof color === 'string') return false;

    return isEmptyColorOption(color);
};

export const hasEmptyColorValue = (colors: Color[]): boolean => colors.some(isEmptyColorOption);

export const resolveColorData = (color: ColorButtonProps['color']) => {
    if (!color) return { colorCss: null, colorName: null };

    if (isObject(color)) {
        return {
            colorCss: color.css,
            colorName: color.name,
        };
    }

    return {
        colorCss: getColorCssValue(color),
        colorName: color,
    };
};
